import { useMemo } from "react";
import { useAuth } from "../utils/AuthContext.tsx";
import { TagValueModel, TagModel, TagOption } from "@/utils/Types.ts";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CornerDownRight } from "lucide-react";
import { useTag } from "@/utils/Api.ts";

interface TagPopoverProperties {
  onSelect?: (value: TagValueModel) => void;
  option: TagOption;
}

interface TagGrouping {
  label: string;
  values: TagValueModel[];
}

function groupTagValues(tag: TagModel): TagGrouping[] {
  if (!tag.scopes) return [];

  const groupings = new Map<string, TagGrouping>();

  for (const scope of tag.scopes) {
    scope.values.sort((a: TagValueModel, b: TagValueModel) => a.ordinal - b.ordinal);

    for (const v of scope.values) {
      const existing = groupings.get(v.group);
      if (existing) {
        existing.values.push(v);
      } else {
        groupings.set(v.group, { label: v.group, values: [v] });
      }
    }
  }

  return Array.from(groupings.values());
}

const TagPopover: React.FC<TagPopoverProperties> = (props: TagPopoverProperties) => {
  const { token, currentAccount } = useAuth();

  //loads all scopes and values for a tag by name
  const { data: tag, isLoading } = useTag(
    props.option?.name ?? undefined,
    currentAccount?.id ?? undefined,
    token ?? undefined
  );

  //builds client-side groups from the tag values (across all scopes)
  const groups: TagGrouping[] | undefined = useMemo(() => {
    if (!tag || !tag.scopes) {
      return undefined;
    }
    return groupTagValues(tag);
  }, [tag]);

  const handleSelect = (value: TagValueModel) => {
    if (props.onSelect) {
      props.onSelect(value);
    }
  };

  const defaultFilter = (value: string, search: string, keywords?: string[]): number => {
    if (props.option && props.option.filter) return props.option.filter(value, search, keywords);
    if (value.toLowerCase().includes(search.toLowerCase())) return 0.1;
    return 0;
  };

  return (
    <Command className="w-[280px]" filter={defaultFilter}>
      <CommandInput
        placeholder="Find tags..."
        autoFocus={true}
        className="h-9"
        onKeyDown={(e: React.KeyboardEvent) => {
          //avoids closing command when using keyboard to clear input
          if (e.shiftKey && e.metaKey && e.key == "ArrowLeft") {
            e.stopPropagation();
          }
        }}
      />
      <CommandList>
        <CommandEmpty>
          {!isLoading && "No results found."}
          {isLoading && "Loading..."}
        </CommandEmpty>
        {groups &&
          groups.map((group, i) => (
            <CommandGroup
              key={i}
              heading={
                <div className="flex items-center gap-x-2">
                  <CornerDownRight className="w-[16px] h-[16px] hidden" />
                  {group.label}
                </div>
              }
              className="text-xs"
            >
              {group.values.map((value: TagValueModel, j: number) => (
                <CommandItem
                  key={j}
                  keywords={[group.label, value.name]}
                  value={`${value.label}`}
                  onSelect={() => {
                    handleSelect(value);
                  }}
                  className="flex flex-row items-center"
                >
                  <span className="grow truncate block pr-4">{value.label}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{value.hint}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
      </CommandList>
    </Command>
  );
};

export default TagPopover;
